import React from 'react'
import {Button, makeStyles, Paper, TextField, Toolbar, FormHelperText} from '@material-ui/core'
import {Formik} from 'formik'
import {Link} from 'react-router-dom'
import {useIntl, FormattedMessage} from 'react-intl'
import * as Yup from 'yup'
import LoaderButton from 'components/atoms/LoaderButton'

const NAME_MAX_LENGTH = 60
const BRAND_MAX_LENGTH = 30
const QUANTITY_MAX = 9999999

const useStyles = makeStyles(theme => ({
  root: {
    margin: theme.spacing(3, 0),
    padding: theme.spacing(3, 2),
  },
  button: {
    marginRight: theme.spacing(2),
  },
}))

const productSchema = Yup.object().shape({
  name: Yup.string()
    .required('formValidations.required')
    .max(NAME_MAX_LENGTH, 'formValidations.maxLength'),
  brand: Yup.string().max(BRAND_MAX_LENGTH, 'formValidations.maxLength'),
  quantity: Yup.number()
    .typeError('formValidations.fieldNumber')
    .integer('formValidations.integer')
    .min(0, 'formValidations.minNumber')
    .max(QUANTITY_MAX, 'formValidations.maxNumber'),
})

export default function ProductForm({
  initialValues,
  onSubmit,
  errorMessage,
  submitLabel,
  loadingText,
  cancelTo,
}) {
  const classes = useStyles()
  const intl = useIntl()

  return (
    <Paper className={classes.root}>
      <Formik
        validationSchema={productSchema}
        initialValues={initialValues}
        onSubmit={onSubmit}
      >
        {({handleSubmit, handleChange, handleBlur, errors, touched, values, isSubmitting}) => (
          <form onSubmit={handleSubmit} noValidate>
            <TextField
              required
              id="name"
              name="name"
              label={intl.formatMessage({id: 'name'})}
              margin="normal"
              fullWidth
              autoFocus
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.name && Boolean(errors.name)}
              helperText={
                touched.name &&
                errors.name &&
                intl.formatMessage({id: errors.name}, {max: NAME_MAX_LENGTH})
              }
              value={values.name}
            />
            <TextField
              id="brand"
              name="brand"
              label={intl.formatMessage({id: 'brand'})}
              margin="normal"
              fullWidth
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.brand && Boolean(errors.brand)}
              helperText={
                touched.brand &&
                errors.brand &&
                intl.formatMessage({id: errors.brand}, {max: BRAND_MAX_LENGTH})
              }
              value={values.brand}
            />
            <TextField
              id="quantity"
              name="quantity"
              label={intl.formatMessage({id: 'quantity'})}
              type="number"
              inputProps={{step: 1}}
              margin="normal"
              fullWidth
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.quantity && Boolean(errors.quantity)}
              helperText={
                touched.quantity &&
                errors.quantity &&
                intl.formatMessage({id: errors.quantity}, {min: 0, max: QUANTITY_MAX})
              }
              value={values.quantity}
            />
            {errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
            <Toolbar disableGutters>
              <LoaderButton
                variant="contained"
                color="primary"
                className={classes.button}
                type="submit"
                isLoading={isSubmitting}
                loadingText={loadingText}
              >
                {submitLabel}
              </LoaderButton>
              <Button
                component={Link}
                to={cancelTo}
                variant="contained"
                className={classes.button}
              >
                <FormattedMessage id="buttons.cancel" />
              </Button>
            </Toolbar>
          </form>
        )}
      </Formik>
    </Paper>
  )
}
